import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert
} from '@mui/material';
import {
  Check as CheckIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import StyledButton from '../components/UI/StyledButton';
import { ROLE_DISPLAY, USER_ROLES } from '../constants/roles';

const InviteSuccessPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get success data from navigation state
  const { 
    recipientEmail, 
    role, 
    childName, 
    message 
  } = location.state || {};

  const getRoleInfo = (role) => {
    switch (role) {
      case 'therapist':
        return {
          label: 'Therapist',
          color: theme.palette.secondary.main,
          description: 'Professional team member'
        };
      case 'caregiver':
        return {
          label: 'Caregiver',
          color: theme.palette.primary.main,
          description: 'Care circle member'
        };
      case 'care_partner':
        return {
          label: 'Care Partner',
          color: theme.palette.primary.main,
          description: ROLE_DISPLAY[USER_ROLES.CARE_PARTNER].inviteLabel
        };
      default:
        return {
          label: 'Team Member',
          color: theme.palette.grey[600],
          description: 'Care circle member'
        };
    }
  };

  const roleInfo = getRoleInfo(role);

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      <Card 
        elevation={0}
        sx={{ 
          borderRadius: { xs: 3, md: 4 },
          border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
          overflow: 'hidden'
        }}
      >
        {/* Success Header */}
        <Box sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.1)} 100%)`,
          p: { xs: 2.5, md: 4 },
          textAlign: 'center'
        }}>
          <CheckIcon sx={{ 
            fontSize: { xs: 48, sm: 64 }, 
            color: theme.palette.success.main, 
            mb: 2 
          }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.6rem', sm: '2.125rem' } }}>
            Invitation Sent!
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
            Your invitation has been delivered successfully
          </Typography>
        </Box>

        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          {/* Success Details */}
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            icon={<SendIcon />}
          >
            {message || `Invitation sent to ${recipientEmail || 'the recipient'}`}
          </Alert>

          {/* Invitation Summary */}
          {recipientEmail && role && childName && (
            <Box sx={{ 
              p: { xs: 2, md: 3 },
              border: `1px solid ${alpha(roleInfo.color, 0.3)}`,
              borderRadius: 2,
              bgcolor: alpha(roleInfo.color, 0.05),
              mb: 3
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Invitation Summary
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Invited:</strong> {recipientEmail}
                </Typography>
                <Typography variant="body2">
                  <strong>Role:</strong> {roleInfo.label}
                </Typography>
                <Typography variant="body2">
                  <strong>Person:</strong> {childName}
                </Typography>
              </Box>
            </Box>
          )}

          {/* What happens next */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              What happens next?
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • The recipient will receive an email invitation
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • They can click the link to join the care circle
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • You'll see them in the care circle once they accept
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center'
          }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/care-team')}
                sx={{ minWidth: { xs: '100%', sm: 150 } }}
              >
                Back to Care Circle
              </Button>
              <StyledButton
                variant="contained"
                onClick={() => navigate('/dashboard')}
                sx={{ minWidth: { xs: '100%', sm: 150 } }}
              >
                Back to Dashboard
              </StyledButton>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default InviteSuccessPage;
